import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATH_INFO, toSafeSampleIdPathSegment } from "../pages/utils/helper";
import { format } from "date-fns";
import { clientFetch } from "../utils/clientFetch";
import { UpdateUserContext } from "./userContext";

export type SamplesResponseType = {
  id: string
  part_number: string
  repeat: number
  size: string
  color_no: string
  qr_code: string
  url: string
  kind: 'sample' | 'final' | null
  time: number
  brand_name: string
  item_name: string
  sample_number: string
  created_at: string
  updated_at: string
  sampleMeasurements: sampleMeasurements[]
  sampleWeightMeasurements: sampleWeightMeasurements[]
  sampleImages: sampleImages[]
  sampleEcImages: sampleImages[]
  companyId: string
  clothtype: string
  device_id?: number // FIXME: 暫定対応
  device_name?: string
  device_location?: string
  is_guest_mode: boolean
  is_admin_mode?: boolean
  department_name?: string | null
}

export type IdSearchSampleResult =
  | { status: 'success', sample: SamplesResponseType }
  | { status: 'not_found' }
  | { status: 'error', statusCode?: number }

export type SampleComparisonColumn = {
  key: string
  label: string
  sample_id: string | null
  kind: 'sample' | 'final'
  time: number
  size: string | null
  exists: boolean
  is_current: boolean
  is_editable: boolean
  is_reference_only: boolean
}

export type SampleComparisonCell = {
  column_key: string
  sample_id: string | null
  spec: number | null
  result_real: number | null
  diff: string | null
  validate: boolean | null
  measure_type: 'auto' | 'manual' | null
}

export type SampleComparisonRow = {
  measure_name: string
  normalized_measure_name: string
  measure_index: number | null
  current_sample_has_measurement: boolean
  cells: SampleComparisonCell[]
}

export type SampleComparisonResponse = {
  mode: 'single' | 'sample-progressive' | 'final-with-samples'
  current_sample_id: string
  is_guest_mode: boolean
  is_admin_mode: boolean
  can_edit_current: boolean
  columns: SampleComparisonColumn[]
  rows: SampleComparisonRow[]
}

type SampleComparisonPageResponse = {
  current_sample: SamplesResponseType
  comparison: SampleComparisonResponse
}

export type sampleMeasurements = {
  measure_index: number
  measure_name: string
  image_index: number
  resolution: number
  result_pixel: number,
  result_real: number,
  result_point: { x: number, y: number }[]
  measure_type: 'auto' | 'manual' | ''
  time: number
  is_deleted: boolean
}

type sampleWeightMeasurements = {
  weight: number
}

export type sampleImages = {
  url: string
  thumbnail_url: string
  image_index: number
  sample_id: string
}

export const sampleDefault: SamplesResponseType = {
  id: '',
  part_number: '',
  repeat: 0,
  size: '',
  color_no: '',
  qr_code: '',
  url: '',
  kind: null,
  time: 0,
  brand_name: '',
  item_name: '',
  sample_number: '',
  created_at: '',
  updated_at: '',
  sampleMeasurements: [],
  sampleWeightMeasurements: [],
  sampleImages: [],
  sampleEcImages: [],
  companyId: '',
  clothtype: '',
  device_id: 0, // FIXME: 暫定対応
  device_name: '',
  device_location: '',
  is_guest_mode: true, // ユーザー情報未取得時は安全側に倒し、必ずゲスト扱いとする
  is_admin_mode: false,
  department_name: null
}

export type DiffType = {
  data: {
    condition: { hasSpec: boolean, hasTolerance: boolean }
    points: Points[]
  }
  status: string
}

type Tolerance = {
  minus: number
  plus: number
}

type Points = {
  measure_name: string
  result_real: number
  spec: number
  diff: number
  validate: boolean
  tolerance: Tolerance[]
}

export const diffDefault: DiffType = {
  data: {
    condition: { hasSpec: false, hasTolerance: false },
    points: []
  },
  status: ''
}

export const SAMPLES_PAGE_LIMIT = 50

export type SamplesMeta = {
  total: number
  page: number
  limit: number
  hasMore: boolean
  nextPage: number | null
}

type SamplesApiResponse = {
  data: SamplesResponseType[]
  meta: SamplesMeta
}

const defaultSamplesMeta: SamplesMeta = {
  total: 0,
  page: 1,
  limit: SAMPLES_PAGE_LIMIT,
  hasMore: false,
  nextPage: null
}

const HTTP_STATUS_UNAUTHORIZED = 401
const HTTP_STATUS_FORBIDDEN = 403
const HTTP_STATUS_BAD_REQUEST = 400

// New type for filter parameters
export type FilterParams = {
  userName?: string;
  partNumber?: string;
  sampleStatus?: string;
  brandName?: string;
  size?: string;
  colorNo?: string;
  departmentId?: number;
  departmentName?: string;
  createdFrom?: Date;
  createdTo?: Date;
};

export const SamplesContext = createContext<{
  samples: SamplesResponseType[]
  sample: SamplesResponseType
  rowSample: SamplesResponseType
  comparison: SampleComparisonResponse | null
  diff: DiffType
  canvasImage?: string | null
  samplesMeta: SamplesMeta
  isSamplesLoading: boolean
}>({
  samples: [],
  sample: sampleDefault,
  rowSample: sampleDefault,
  comparison: null,
  diff: diffDefault,
  canvasImage: null,
  samplesMeta: defaultSamplesMeta,
  isSamplesLoading: false
})

export const UpdateSamplesContext = createContext<{
  updateRowSample: (value: Partial<SamplesResponseType>) => void
  resetCurrentSample: () => void
  increment: () => void
  decrement: () => void
  refreshSamples: (filters?: FilterParams, options?: { append?: boolean }) => Promise<void>
  loadMoreSamples: () => Promise<void>
  updateSample: (obj: SamplesResponseType) => void
  idSearchSample: (id: string, shouldSetState: boolean) => Promise<IdSearchSampleResult>
  loadMeasurementComparison: (sampleId: string) => Promise<IdSearchSampleResult>
  saveSample: (obj: SamplesResponseType, isUpdate: boolean) => Promise<[boolean, number]>
  fetchUrlQrSample: (sampleUrl?: string, file?: File) => Promise<void>
  fetchDiff: (sample: SamplesResponseType) => Promise<void>
  fetchQrCode: (sampleId: string) => Promise<{ id: string, url: string, qr_code: string } | null>
  updateCanvasImage: (imageDataUrl: string) => void
}>({
  updateRowSample: (value: Partial<SamplesResponseType>) => { },
  resetCurrentSample: () => { },
  increment: () => { },
  decrement: () => { },
  refreshSamples: async (filters?: FilterParams) => { },
  loadMoreSamples: async () => { },
  updateSample: (obj: SamplesResponseType) => { },
  idSearchSample: async (id: string, shouldSetState: boolean) => ({ status: 'error' }),
  loadMeasurementComparison: async (sampleId: string) => ({ status: 'error' }),
  saveSample: async (obj: SamplesResponseType, isUpdate: boolean) => [false, 0],
  fetchUrlQrSample: async (sampleUrl?: string, file?: File) => { },
  fetchDiff: async (sample: SamplesResponseType) => { },
  fetchQrCode: async (sampleId: string) => null,
  updateCanvasImage: (imageDataUrl: string) => { },
})

export const buildSamplesQueryParams = (filters: FilterParams | undefined, page: number, limit: number) => {
  const params = new URLSearchParams()
  params.append('page', String(page))
  params.append('limit', String(limit))

  if (filters?.userName) params.append('user_name', filters.userName)
  if (filters?.partNumber) params.append('part_number', filters.partNumber)
  if (filters?.sampleStatus) params.append('sample_status', filters.sampleStatus)
  if (filters?.brandName) params.append('brand_name', filters.brandName)
  if (filters?.size) params.append('size', filters.size)
  if (filters?.colorNo) params.append('color_no', filters.colorNo)
  if (filters?.departmentId) params.append('department_id', String(filters.departmentId))
  if (filters?.departmentName) params.append('department_name', filters.departmentName)
  if (filters?.createdFrom) params.append('created_from', format(filters.createdFrom, 'yyyy-MM-dd'))
  if (filters?.createdTo) params.append('created_to', format(filters.createdTo, 'yyyy-MM-dd'))

  return params.toString()
}

type NavigateFn = (path: string) => void
type AuthErrorMode = 'forbidden-only' | 'unauthorized-and-forbidden'

export type FetchDepartmentSamplesOptions = {
  filters?: FilterParams
  page: number
  limit: number
  navigate?: NavigateFn
  authErrorMode?: AuthErrorMode
  shouldReturnOnAuthError?: boolean
}

export type FetchDepartmentSamplesResult = {
  items: SamplesResponseType[]
  meta: SamplesMeta
} | null

const getAuthErrorStatuses = (mode: AuthErrorMode) => {
  if (mode === 'unauthorized-and-forbidden') {
    return [HTTP_STATUS_UNAUTHORIZED, HTTP_STATUS_FORBIDDEN]
  }
  return [HTTP_STATUS_FORBIDDEN]
}

export const fetchDepartmentSamples = async (options: FetchDepartmentSamplesOptions): Promise<FetchDepartmentSamplesResult> => {
  const {
    filters,
    page,
    limit,
    navigate,
    authErrorMode = 'forbidden-only',
    shouldReturnOnAuthError = true
  } = options

  let url = `${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/department`
  const queryString = buildSamplesQueryParams(filters, page, limit)
  if (queryString) {
    url = `${url}?${queryString}`
  }

  const res = await clientFetch(url, {
    method: "GET",
    headers: {
      'Content-Type': 'application/json',
    }
  })

  const authErrorStatuses = getAuthErrorStatuses(authErrorMode)
  if (authErrorStatuses.includes(res.status)) {
    if (navigate) {
      navigate(PATH_INFO.LOGIN.PATH)
    }
    if (shouldReturnOnAuthError) {
      return null
    }
  }

  if (!res.ok) {
    throw new Error("Network response was not ok")
  }

  const data = await res.json() as SamplesApiResponse
  return {
    items: data.data ?? [],
    meta: data.meta ?? defaultSamplesMeta
  }
}

const isSampleResponse = (value: unknown): value is SamplesResponseType => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const sample = value as Partial<SamplesResponseType>
  return typeof sample.id === 'string'
    && Array.isArray(sample.sampleMeasurements)
    && Array.isArray(sample.sampleWeightMeasurements)
    && Array.isArray(sample.sampleImages)
    && Array.isArray(sample.sampleEcImages)
}

const isSampleComparisonResponse = (value: unknown): value is SampleComparisonResponse => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const comparison = value as Partial<SampleComparisonResponse>
  return typeof comparison.current_sample_id === 'string'
    && Array.isArray(comparison.columns)
    && Array.isArray(comparison.rows)
}

const isSampleComparisonPageResponse = (value: unknown): value is SampleComparisonPageResponse => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const data = value as Partial<SampleComparisonPageResponse>
  return isSampleResponse(data.current_sample) && isSampleComparisonResponse(data.comparison)
}

export function useSamplesContext() {
  const { setIsGuest, setIsAdminMode } = useContext(UpdateUserContext)
  const navigate = useNavigate()
  const [samples, setSamples] = useState<SamplesResponseType[]>([])
  const [sample, setSample] = useState(sampleDefault)
  const [rowSample, setRowSample] = useState(sampleDefault)
  const [comparison, setComparison] = useState<SampleComparisonResponse | null>(null)
  const [diff, setDiff] = useState<DiffType>(diffDefault)
  const [canvasImage, setCanvasImage] = useState<string | null>(null)
  const [samplesMeta, setSamplesMeta] = useState<SamplesMeta>(defaultSamplesMeta)
  const [isSamplesLoading, setIsSamplesLoading] = useState<boolean>(false)
  const samplesMetaRef = useRef<SamplesMeta>(defaultSamplesMeta)
  const activeFiltersRef = useRef<FilterParams | undefined>(undefined)

  useEffect(() => {
    samplesMetaRef.current = samplesMeta
  }, [samplesMeta])

  const updateCanvasImage = useCallback((imageDataUrl: string) => {
    setCanvasImage(imageDataUrl)
  }, [])

  const updateRowSample = useCallback((value: Partial<SamplesResponseType>) => {
    setRowSample((prev) => ({ ...prev, ...value }))
  }, [])

  const resetCurrentSample = useCallback(() => {
    setSample(sampleDefault)
    setRowSample(sampleDefault)
    setComparison(null)
    setDiff(diffDefault)
    setCanvasImage(null)
  }, [])

  const increment = useCallback(() => {
    setRowSample((prev) => ({ ...prev, repeat: prev.repeat + 1 }))
  }, [])

  const decrement = useCallback(() => {
    setRowSample((prev) => ({ ...prev, repeat: prev.repeat - 1 }))
  }, [])

  const mergeSamples = useCallback((current: SamplesResponseType[], next: SamplesResponseType[]) => {
    if (current.length === 0) return next
    const existingIds = new Set(current.map((item) => item.id))
    const merged = [...current]
    next.forEach((item) => {
      if (!existingIds.has(item.id)) {
        merged.push(item)
      }
    })
    return merged
  }, [])

  const refreshSamples = useCallback(async (filters?: FilterParams, options?: { append?: boolean }) => {
    const shouldAppend = options?.append ?? false
    const meta = samplesMetaRef.current
    const targetPage = shouldAppend ? (meta.nextPage ?? meta.page + 1) : 1
    if (shouldAppend && !meta.hasMore) {
      return
    }

    const normalizedFilters = !shouldAppend ? (filters ? { ...filters } : undefined) : undefined
    const filtersToUse = shouldAppend ? activeFiltersRef.current : normalizedFilters
    if (!shouldAppend) {
      activeFiltersRef.current = normalizedFilters
    }

    try {
      setIsSamplesLoading(true)

      const result = await fetchDepartmentSamples({
        filters: filtersToUse,
        page: targetPage,
        limit: SAMPLES_PAGE_LIMIT,
        navigate,
        shouldReturnOnAuthError: false
      })
      if (!result) {
        return
      }

      const { items, meta: nextMeta } = result
      setSamples((prev) => shouldAppend ? mergeSamples(prev, items) : items)
      setSamplesMeta(nextMeta)
    } catch (error) {
      console.error("Error fetching samples.");
    } finally {
      setIsSamplesLoading(false)
    }
  }, [navigate, mergeSamples])

  const loadMoreSamples = useCallback(async () => {
    if (isSamplesLoading || !samplesMetaRef.current.hasMore) return
    await refreshSamples(undefined, { append: true })
  }, [isSamplesLoading, refreshSamples])

  const updateSample = useCallback((obj: SamplesResponseType) => {
    if (!obj) return
    setSample(obj)
  }, [])

  const applySampleAccessMode = useCallback((data: { is_guest_mode?: unknown, is_admin_mode?: unknown }) => {
    const isGuestMode = typeof data.is_guest_mode === 'boolean' ? data.is_guest_mode : true
    const isAdminMode = typeof data.is_admin_mode === 'boolean' ? data.is_admin_mode : null
    setIsGuest(isGuestMode)
    setIsAdminMode(isAdminMode)
  }, [setIsGuest, setIsAdminMode])

  const idSearchSample = useCallback(async (id: string, shouldSetState: boolean): Promise<IdSearchSampleResult> => {
    const safeSampleId = toSafeSampleIdPathSegment(id)
    if (!safeSampleId) {
      setIsAdminMode(null)
      return { status: 'error', statusCode: HTTP_STATUS_BAD_REQUEST }
    }

    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${safeSampleId}`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: false
      })

      if (res.status === 404) {
        setIsAdminMode(null)
        return { status: 'not_found' }
      }

      if (!res.ok) {
        setIsAdminMode(null)
        return { status: 'error', statusCode: res.status }
      }

      const data = await res.json();
      applySampleAccessMode(data)
      if (shouldSetState) {
        setSample(data)
        setRowSample(data)
        setComparison(null)
      }
      return { status: 'success', sample: data }
    } catch (error) {
      console.error("Error fetching sample.", error);
      setIsAdminMode(null)
      return { status: 'error' }
    }
  }, [applySampleAccessMode, setIsAdminMode])

  const loadMeasurementComparison = useCallback(async (sampleId: string): Promise<IdSearchSampleResult> => {
    const safeSampleId = toSafeSampleIdPathSegment(sampleId)
    if (!safeSampleId) {
      setIsAdminMode(null)
      setComparison(null)
      return { status: 'error', statusCode: HTTP_STATUS_BAD_REQUEST }
    }

    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${safeSampleId}/comparison`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: false
      })

      if (res.status === 404) {
        setIsAdminMode(null)
        setComparison(null)
        return { status: 'not_found' }
      }

      if (!res.ok) {
        setIsAdminMode(null)
        setComparison(null)
        return { status: 'error', statusCode: res.status }
      }

      const data = await res.json()
      if (!isSampleComparisonPageResponse(data)) {
        setIsAdminMode(null)
        setComparison(null)
        return { status: 'error', statusCode: HTTP_STATUS_BAD_REQUEST }
      }

      applySampleAccessMode(data.current_sample)
      setSample(data.current_sample)
      setRowSample(data.current_sample)
      setComparison(data.comparison)
      setDiff(diffDefault)
      return { status: 'success', sample: data.current_sample }
    } catch (error) {
      console.error("Error fetching sample comparison.", error);
      setIsAdminMode(null)
      setComparison(null)
      return { status: 'error' }
    }
  }, [applySampleAccessMode, setIsAdminMode])

  const saveSample = useCallback(async (obj: SamplesResponseType, isUpdate: boolean): Promise<[boolean, number]> => {
    const { url, qr_code, sampleImages, sampleEcImages, sampleWeightMeasurements, ...otherData } = obj
    const normalizedData = {
      ...otherData,
      time: otherData.kind === 'final' ? 1 : otherData.time,
    }
    const safeSampleId = toSafeSampleIdPathSegment(normalizedData.id)
    if (!safeSampleId) {
      return [false, HTTP_STATUS_BAD_REQUEST]
    }

    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/save/${safeSampleId}`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...normalizedData, isUpdate }),
        handleUnauthorized: true
      })
      if (res.status === 401) {
        return [false, res.status]
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH)
        return [false, res.status]
      }

      if (!res.ok) {
        return [false, res.status]
      }

      const data = await res.json();
      await loadMeasurementComparison(data.id)
      refreshSamples()
      return [true, res.status]
    } catch (error) {
      console.error("Error fetching samples.");
      return [false, 500]
    }
  }, [navigate, refreshSamples, loadMeasurementComparison])

  const fetchUrlQrSample = useCallback(async (sampleUrl?: string, file?: File) => {
    try {
      const headers: Record<string, string> = {}
      let body: FormData | string
      let options: RequestInit

      if (file) {
        const formData = new FormData()
        formData.append('file', file)
        body = formData
        options = {
          method: 'POST',
          headers,
          body,
        }
      } else if (sampleUrl) {
        headers['Content-Type'] = 'application/json'
        body = JSON.stringify({ sample_url: sampleUrl })
        options = {
          method: 'POST',
          headers,
          body,
        }
      } else {
        throw new Error('ファイルまたはURLいずれかが必要です')
      }

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/qr-code`, {
        ...options,
        handleUnauthorized: true
      })

      if (res.status === 401) {
        return
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (!res.ok) {
        throw new Error('ERROR UPLOAD')
      }

      const data = await res.json();
      if (typeof data?.is_guest_mode === "boolean") {
        setIsGuest(data.is_guest_mode)
      }
      if (typeof data?.is_admin_mode === "boolean") {
        setIsAdminMode(data.is_admin_mode)
      } else {
        // is_admin_mode 未含有時は安全側に倒し、未確定（null）扱いとする
        setIsAdminMode(null)
      }
      setSample(data)
      setRowSample(data)
      setComparison(null)
    } catch (error) {
      console.error('Error fetching sample')
    }
  }, [navigate, setIsGuest, setIsAdminMode])

  const fetchDiff = useCallback(async (sample: SamplesResponseType) => {
    const baseUrl = `${process.env.REACT_APP_API_ENDPOINT}/apis/client/sewing-spec/diff`
    const url = sample.companyId ? `${baseUrl}/${sample.companyId}` : baseUrl

    const newMeasurements = sample.sampleMeasurements.filter((sm) => !sm.is_deleted)

    try {
      const res = await clientFetch(url, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          part_number: sample.part_number,
          sample_number: sample.sample_number,
          repeat: sample.repeat,
          time: sample.time,
          brand_name: sample.brand_name,
          measurements: newMeasurements,
          size: sample.size,
          kind: sample.kind,
          color_no: sample.color_no || null,
        }),
        handleUnauthorized: false
      })

      const data = await res.json()
      setDiff(data)
    } catch (error) {
      console.log('ERROR FETCH')
    }
  }, [])

  const fetchQrCode = useCallback(async (sampleId: string): Promise<{ id: string, url: string, qr_code: string } | null> => {
    const safeSampleId = toSafeSampleIdPathSegment(sampleId)
    if (!safeSampleId) {
      return null
    }

    try {
      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/samples/${safeSampleId}/qr-code`, {
        method: "GET",
        headers: {
          'Content-Type': 'application/json',
        },
        handleUnauthorized: true
      });

      if (res.status === 401) {
        return null;
      }
      if (res.status === 403) {
        navigate(PATH_INFO.LOGIN.PATH);
        return null;
      }

      if (!res.ok) {
        throw new Error('QRコードの取得に失敗しました。');
      }
      return await res.json();
    } catch (error) {
      console.error("Error fetching QR");
      return null;
    }
  }, [navigate]);
  return {
    samples,
    sample,
    rowSample,
    comparison,
    diff,
    canvasImage,
    samplesMeta,
    isSamplesLoading,
    updateCanvasImage,
    updateRowSample,
    resetCurrentSample,
    increment,
    decrement,
    refreshSamples,
    loadMoreSamples,
    updateSample,
    idSearchSample,
    loadMeasurementComparison,
    saveSample,
    fetchUrlQrSample,
    fetchDiff,
    fetchQrCode,
  }
}

export function SamplesProvider({ children }: { children: React.ReactNode }) {
  const { samples, sample, rowSample, comparison, diff, canvasImage, samplesMeta, isSamplesLoading, updateCanvasImage, updateRowSample, resetCurrentSample, increment, decrement, refreshSamples, loadMoreSamples, updateSample, idSearchSample, loadMeasurementComparison, saveSample, fetchUrlQrSample, fetchDiff, fetchQrCode } = useSamplesContext()

  return (
    <UpdateSamplesContext.Provider value={{ updateCanvasImage, updateRowSample, resetCurrentSample, increment, decrement, refreshSamples, loadMoreSamples, updateSample, idSearchSample, loadMeasurementComparison, saveSample, fetchUrlQrSample, fetchDiff, fetchQrCode }}>
      <SamplesContext.Provider value={{ samples, sample, rowSample, comparison, diff, canvasImage, samplesMeta, isSamplesLoading }}>
        {children}
      </SamplesContext.Provider>
    </UpdateSamplesContext.Provider>
  )
}
